<template>
  <f7-page :page-content="false" with-subnavbar>
    <!-- with-subnavbar is REQUIRED (same as HomePage / SupplierWallPostsPage):
         it is what adds the segment bar's height to the scroller's top padding.
         Left to Framework7 the class arrives only on the PUSHED carton route
         (core adds it on pageInit) and never on the `/orders/` tab (a routable
         tab fires no pageInit), so on the tab everything at the top of the
         scroller — the decisions banner first — started underneath the bar.
         This note sits INSIDE the page on purpose: the component is also a
         pushed page, and a comment at the template root makes it a Fragment. -->
    <!-- Navbar -->
    <f7-navbar>
      <f7-nav-left>
        <!-- Reached over the carton route (/orders/box/:boxId/, tips-wave
             P3-A) rather than the `/orders/` tab — a pushed page needs a way
             back that the tab never does. A manual `<f7-link>` rather than
             `f7-navbar`'s own `back-link` prop: that prop renders its OWN
             `<f7-nav-left>` wrapper (F7-Vue's hasLeft = backLink || slots),
             which would sit ALONGSIDE this one — a plain child, not a named
             slot — producing two nav-left regions instead of one. -->
        <f7-link
          v-if="isBoxRoute"
          icon-f7="chevron_right"
          :aria-label="$t('general.back')"
          class="nav-icon-link"
          @click="handleBoxRouteBack"
        ></f7-link>
        <f7-link
          v-if="!isFetchingAll"
          icon-material="autorenew"
          :aria-label="$t('general.refresh')"
          class="nav-icon-link"
          @click="handleRefreshClick"
        ></f7-link>
        <f7-preloader v-else :size="24" color="multi"></f7-preloader>
      </f7-nav-left>
      <f7-nav-title>{{ $t('tabs.orders') }}</f7-nav-title>
      <f7-nav-right>
        <!-- The add-box CTA lives in the thumb-reachable FAB below (UX-11). -->
        <!-- !canOrder + AWAITING_DEPOSIT: wallet shortcut -->
        <f7-link
          v-if="viewMode === 'active' && stateKey === 'APPROVED_AWAITING_DEPOSIT'"
          icon-f7="arrow_down_circle_fill"
          :aria-label="$t('profile.membership.state.APPROVED_FULL.depositCta')"
          class="nav-icon-link"
          @click="handleAwaitingDepositCta"
        ></f7-link>
      </f7-nav-right>

      <!-- Subnavbar for filters -->
      <f7-subnavbar :inner="false">
        <!-- View Mode Segmented -->
        <f7-segmented dir="ltr" strong tag="div" class="margin-bottom">
          <f7-button :active="viewMode === 'closed'" @click="viewMode = 'closed'">
            {{ $t('ordersPage.tabClosed') }}
            <f7-badge v-if="ClosedBoxes.length > 0" color="gray">
              {{ ClosedBoxes.length }}
            </f7-badge>
          </f7-button>
          <f7-button :active="viewMode === 'active'" @click="viewMode = 'active'">
            {{ $t('ordersPage.tabActive') }}
            <f7-badge v-if="OpenedBoxes.length > 0" color="blue">
              {{ OpenedBoxes.length }}
            </f7-badge>
          </f7-button>
        </f7-segmented>
      </f7-subnavbar>
    </f7-navbar>

    <!-- BLOCKED: red top banner — view-only mode -->
    <f7-toolbar v-if="stateKey === 'BLOCKED'" top class="lifecycle-blocked-banner">
      <div class="lifecycle-blocked-banner__inner">
        <f7-icon f7="xmark_octagon_fill" size="14" class="lifecycle-blocked-banner__icon"></f7-icon>
        <span class="lifecycle-blocked-banner__text">
          {{ $t('orders.lifecycle.blocked.banner') }}
        </span>
      </div>
    </f7-toolbar>

    <!-- Page Content -->
    <f7-page-content
      ref="pageContentRef"
      ptr
      ptr-mousewheel
      hide-navbar-on-scroll
      class="orders-page-content"
      @ptr:refresh="handlePullToRefresh"
    >
      <!-- Everything above the items list — observed so the virtualizer's
           scrollMargin tracks its height (swiper/summary load async) -->
      <div ref="aboveListRef">
        <!-- Member-answerable collection issues: a store-derived entry to the
             "قرارات مطلوبة" hub. Self-hides when nothing awaits the member's
             decision; costs no extra fetch (reads the loaded OrderBoxItem store).
             The slot only gives it the page's side gutter; it has no height of
             its own, so it leaves no gap when the banner hides itself. -->
        <div class="orders-decisions-slot">
          <decisions-inbox-banner></decisions-inbox-banner>
        </div>

        <!-- Boxes: first-load skeleton -->
        <div
          v-if="isFetchingAll && displayedBoxes.length === 0"
          class="packages-swiper-wrapper"
          aria-hidden="true"
        >
          <f7-skeleton-block class="package-skeleton skeleton-effect-wave"></f7-skeleton-block>
        </div>

        <!-- Boxes: load failed with nothing cached — distinct error state with retry
           (UX-7: a toast alone used to collapse into a false "no orders" view) -->
        <f7-block
          v-else-if="fetchAllError && displayedBoxes.length === 0"
          class="text-align-center boxes-error-state"
        >
          <f7-icon f7="wifi_exclamationmark" size="48" color="orange"></f7-icon>
          <p class="text-color-gray">{{ $t('errors.loadFailed') }}</p>
          <f7-button large outline class="retry-button" @click="handleRetryFetch">
            {{ $t('general.retry') }}
          </f7-button>
        </f7-block>

        <!-- Packages Swiper -->
        <div v-else-if="displayedBoxes.length > 0" class="packages-swiper-wrapper">
          <!-- Swipe discoverability — persistent box counter, live via
               selectedPackageIndex/onPackageChange. Hidden with a single box. -->
          <div v-if="hasMultipleBoxes" class="swipe-counter-pill">
            <f7-icon f7="cube_box" size="13" class="swipe-counter-pill__icon"></f7-icon>
            <span class="swipe-counter-pill__text">
              {{
                $t('box.swipe.counter', {
                  current: selectedPackageIndex + 1,
                  total: displayedBoxes.length,
                })
              }}
            </span>
          </div>

          <!-- Swipe discoverability (option ج) — the swiper plus persistent
               prev/next edge chevrons, vertically centered on the card body.
               Chevrons make "there's more" always-visible and give a tap path
               alongside the swipe gesture. Hidden with a single box. -->
          <div class="swipe-viewport">
            <swiper-container
              ref="swiperEl"
              :slides-per-view="1.1"
              :space-between="16"
              centered-slides
              pagination
              @swiperslidechange="onPackageChange"
            >
              <swiper-slide v-for="box in displayedBoxes" :key="box.id">
                <package-card
                  :box="box"
                  :is-updating="isUpdatingBox && updatingBoxId === box.id"
                  @update:box="handleUpdateBox"
                  @close:box="handleCloseBox"
                  @delete:box="handleDeleteBox"
                ></package-card>
              </swiper-slide>
            </swiper-container>

            <!-- Positioned at the inline-start/end edges so the sides follow
                 text direction (RTL: next box sits to the left). Icons mirror
                 under [dir='rtl'] via the non-scoped rule in <style>. Bound by
                 function (slidePrev/slideNext) and faded at the list bounds. -->
            <button
              v-if="hasMultipleBoxes"
              type="button"
              class="swipe-nav swipe-nav--prev"
              :class="{ 'swipe-nav--disabled': atStart }"
              :disabled="atStart"
              :aria-label="$t('general.previous')"
              @click="goPrevBox"
            >
              <f7-icon :f7="prevChevronIcon" size="19"></f7-icon>
            </button>
            <button
              v-if="hasMultipleBoxes"
              type="button"
              class="swipe-nav swipe-nav--next"
              :class="{ 'swipe-nav--disabled': atEnd }"
              :disabled="atEnd"
              :aria-label="$t('general.next')"
              @click="goNextBox"
            >
              <f7-icon :f7="nextChevronIcon" size="19"></f7-icon>
            </button>
          </div>
        </div>

        <!-- Empty State for No Packages -->
        <f7-block v-else class="text-align-center">
          <!-- Lifecycle-specific empty states (active view only) -->
          <template v-if="viewMode === 'active' && !canOrder">
            <!-- AWAITING_DEPOSIT: prompt to deposit -->
            <template v-if="stateKey === 'APPROVED_AWAITING_DEPOSIT'">
              <f7-icon f7="creditcard_fill" size="80" color="orange"></f7-icon>
              <h3>{{ $t('orders.lifecycle.awaiting.empty.title') }}</h3>
              <p class="text-color-gray">{{ $t('orders.lifecycle.awaiting.empty.description') }}</p>
              <f7-button fill large color="orange" @click="handleSupportCta">
                {{ $t('orders.lifecycle.awaiting.empty.cta') }}
              </f7-button>
              <f7-button large class="lifecycle-cta-secondary" @click="handleAwaitingDepositCta">
                {{ $t('profile.membership.state.APPROVED_FULL.depositCta') }}
              </f7-button>
            </template>
            <!-- PENDING: contact-first CTA — was a dead end with no action at all -->
            <template v-else-if="stateKey === 'PENDING'">
              <f7-icon f7="clock_fill" size="80" color="gray"></f7-icon>
              <h3>{{ $t('orders.lifecycle.pending.empty.title') }}</h3>
              <p class="text-color-gray">{{ $t('orders.lifecycle.pending.empty.description') }}</p>
              <f7-button fill large color="orange" @click="handleSupportCta">
                {{ $t('orders.lifecycle.pending.empty.cta') }}
              </f7-button>
            </template>
            <!-- BLOCKED: support CTA -->
            <template v-else-if="stateKey === 'BLOCKED'">
              <f7-icon f7="xmark_octagon_fill" size="80" color="red"></f7-icon>
              <h3>{{ $t('orders.lifecycle.blocked.history.hint') }}</h3>
              <f7-button fill large color="red" @click="handleSupportCta">
                {{ $t('profile.membership.state.BLOCKED.cta') }}
              </f7-button>
            </template>
            <!-- REJECTED / other: generic locked state -->
            <template v-else>
              <f7-icon f7="xmark_circle_fill" size="80" color="gray"></f7-icon>
              <h3>{{ $t('box.errors.cannotCreateNoActiveMembership') }}</h3>
            </template>
          </template>

          <!-- Normal empty state (canOrder or closed tab) -->
          <template v-else>
            <f7-icon
              :f7="viewMode === 'active' ? 'bag_fill' : 'archivebox_fill'"
              size="80"
              color="gray"
            ></f7-icon>
            <h3>{{ $t('box.empty.noBoxes') }}</h3>
            <p class="text-color-gray">
              {{ viewMode === 'active' ? $t('box.empty.noActive') : $t('box.empty.noClosed') }}
            </p>
            <f7-button
              v-if="viewMode === 'active'"
              fill
              large
              :disabled="isCreatingBox || isAtBoxLimit"
              @click="handleAddBoxClick"
            >
              <f7-icon f7="plus" class="margin-right-half"></f7-icon>
              {{
                isAtBoxLimit
                  ? $t('box.limit.messageShort', { max: MAX_ACTIVE_BOXES })
                  : $t('box.actions.addNew')
              }}
            </f7-button>
          </template>
        </f7-block>

        <!-- Lifecycle info card — rendered when a createBox call was blocked by
           membership state (auth.no_active_membership). Cleared on next attempt. -->
        <lifecycle-info-card
          v-if="showMembershipBlockCard"
          :state-key="membershipStateKey"
        ></lifecycle-info-card>

        <!-- Enhanced Filter Chips Section -->
        <div v-if="selectedPackage && selectedPackage.items" class="filters-wrapper">
          <div class="chips-container">
            <!-- Which chips, in what order, and which one the carton opens on
                 come from `cartonFilterPlan` (carton «نواقص» tab): while the
                 carton can still take a missing item back, «نواقص» is first,
                 amber, and the default; with nothing missing it is not drawn. -->
            <f7-chip
              v-for="filter in itemFilters"
              :key="filter.value"
              :text="filter.label"
              :outline="activeFilter !== filter.value"
              :class="{ 'filter-chip--oos': filter.value === ItemFilter.OUT_OF_STOCK && filterPlan.oosLive }"
              @click="chooseFilter(filter.value)"
            >
              <template #media>
                <f7-badge :color="activeFilter === filter.value ? 'blue' : 'gray'">
                  {{ getFilterCount(filter.value) }}
                </f7-badge>
              </template>
            </f7-chip>
          </div>

          <!-- Advanced Filter Button -->
          <div class="filter-button-wrapper">
            <f7-button
              small
              round
              :fill="hasAdvancedFilters"
              :outline="!hasAdvancedFilters"
              @click="sheetAdvancedFiltersOpened = true"
            >
              <f7-icon f7="line_horizontal_3_decrease_circle" size="20"></f7-icon>
            </f7-button>
          </div>
        </div>

        <!-- ONE explanation for the whole «نواقص» tab, instead of the same
             paragraph inside every item's sheet: we keep looking while the
             carton is open, she can name a shop, or cancel and get the money
             released. Only while that is true (`oosLive`). -->
        <div v-if="isLiveOosTab" class="oos-hint" role="note">
          <div class="oos-hint__icon">
            <f7-icon f7="search" size="18"></f7-icon>
          </div>
          <div class="oos-hint__text">
            <p class="oos-hint__title">{{ $t('box.oos.hint.title') }}</p>
            <p class="oos-hint__body">{{ $t('box.oos.hint.body') }}</p>
          </div>
        </div>

        <!-- Filtered Items Summary Card -->
        <f7-card v-if="selectedPackage" class="items-summary-card">
          <f7-card-content class="summary-content">
            <!-- Left: Items Count -->
            <div class="summary-item">
              <div class="summary-icon-wrapper summary-icon-items">
                <f7-icon f7="cube_box_fill" size="18"></f7-icon>
              </div>
              <div class="summary-details">
                <div class="summary-value">{{ filteredItemsQty }}</div>
                <div class="summary-label">{{ $t('box.stats.piece') }}</div>
              </div>
            </div>

            <!-- Divider -->
            <div class="summary-divider" />

            <!-- Right: Total Amount -->
            <div class="summary-item summary-item-highlight">
              <!-- In the live «نواقص» tab this sum is money HELD for lines nobody
                   has bought — not a total she will pay — so it says so. -->
              <div class="summary-icon-wrapper summary-icon-amount">
                <f7-icon :f7="isLiveOosTab ? 'lock_fill' : 'money_dollar_circle_fill'" size="18"></f7-icon>
              </div>
              <div class="summary-details">
                <div class="summary-value summary-value-primary">{{ formatFilteredTotal }}</div>
                <div class="summary-label">
                  {{ isLiveOosTab ? $t('box.oos.heldLabel') : $t('box.stats.total') }}
                </div>
              </div>
            </div>
          </f7-card-content>
        </f7-card>
      </div>

      <!-- Items List Section -->
      <div v-if="selectedPackage" class="items-section">
        <!-- Items List — virtualized (P9): only the visible window of rows is
             mounted. Rows are absolutely positioned <li> inside f7-list's own
             <ul> so the F7 list DOM contract (ul > li) holds; style/data-index
             fall through to the li because ItemCard is single-root (B7). -->
        <f7-list
          v-if="filteredItems.length > 0"
          ref="itemsListRef"
          media-list
          strong-ios
          outline-ios
          dividers-ios
          class="items-virtual-list"
          :style="{ '--items-virtual-height': `${itemsTotalSize}px` }"
        >
          <item-card
            v-for="virtualRow in virtualRows"
            :key="filteredItems[virtualRow.index].id"
            :ref="measureRow"
            :item="filteredItems[virtualRow.index]"
            :box="selectedPackage"
            :oos-actions="isLiveOosTab"
            :data-index="virtualRow.index"
            class="items-virtual-row"
            :style="{ transform: `translateY(${virtualRow.start - listScrollMargin}px)` }"
            @select="handleItemSelect"
            @suggest="handleItemSuggest"
            @cancel="handleItemCancel"
          ></item-card>
        </f7-list>

        <!-- Empty Items State -->
        <f7-block v-else-if="!isFetchingBox" class="text-align-center">
          <f7-icon f7="cube_box" size="48" color="gray"></f7-icon>
          <p class="text-color-gray">{{ $t('ordersPage.noItemsInTab') }}</p>
        </f7-block>

        <!-- Request Shipping Button — "ready to pack" (ship-ready pack). The state
             is decided from the carton's own lines by `shipGate`: still reading
             them → ISSUE she must resolve → lines still on their way → nothing
             has arrived → ask. The server enforces the same order with a 409.

             THE TWO LANES (done-ordering pack). `CartonDoneOrdering` owns the slot
             of an ACTIVE carton: «خلصت طلباتي» above this block, the closed-carton
             banners, and the team's review card. This block is the FAST LANE and
             is passed in as the slot — unchanged, except that on an open or a
             being-prepared carton it steps down from fill to outline and says so.
             Poola and anything the helper does not own get today's slot as is. -->
        <carton-done-ordering
          v-if="canRequestShipping"
          :box="selectedPackage"
          :review="reviewState"
          :shipping="isRequestingShipping"
          @ship="confirmRequestShipping"
        >
          <template #default="{ fastLane }">
            <f7-block>
              <!-- Decide nothing and claim nothing until the lines are in. -->
              <f7-button
                v-if="shipGate.state === ShipGate.LOADING"
                fill
                large
                disabled
                color="gray"
              >
                <f7-preloader size="20" class="margin-right-half"></f7-preloader>
                {{ $t('box.actions.requestShipping') }}
              </f7-button>
              <f7-button
                v-else-if="shipGate.state === ShipGate.ISSUES"
                fill
                large
                disabled
                color="gray"
              >
                {{ $t('box.issues.resolveFirst') }}
              </f7-button>
              <template v-else-if="shipGate.state === ShipGate.ON_ITS_WAY">
                <f7-button
                  fill
                  large
                  disabled
                  color="gray"
                >
                  {{ $t('box.shipGate.onTheWay', { count: shipGate.onItsWayCount }) }}
                </f7-button>
                <p class="ship-gate-help text-color-gray">{{ $t('box.shipGate.onTheWayHelp') }}</p>
              </template>
              <f7-button
                v-else-if="shipGate.state === ShipGate.NOTHING_ARRIVED"
                fill
                large
                disabled
                color="gray"
              >
                {{ $t('box.shipGate.nothingArrived') }}
              </f7-button>
              <template v-else>
                <f7-button
                  :fill="!fastLane"
                  :outline="fastLane"
                  large
                  color="green"
                  :disabled="isRequestingShipping"
                  @click="confirmRequestShipping"
                >
                  <f7-preloader
                    v-if="isRequestingShipping"
                    size="20"
                    class="margin-right-half"
                  ></f7-preloader>
                  <f7-icon v-else f7="paperplane_fill" class="margin-right-half"></f7-icon>
                  {{ $t('box.actions.requestShipping') }}
                </f7-button>
                <!-- The fast lane says so: she can skip the team's review. -->
                <p v-if="fastLane" class="ship-gate-help text-color-gray">
                  {{ $t('box.doneOrdering.fastLaneHelp') }}
                </p>
              </template>
            </f7-block>
          </template>
        </carton-done-ordering>

        <!-- THE SLOT A READY CARTON USED TO LEAVE EMPTY. `canRequestShipping`
             is ACTIVE-only, so from the moment she asked to ship there was no
             action on the carton at all — and, since the hold work, possibly
             lines in it nobody could find and she cannot now do anything about.
             Re-open is hers until the packaging team seals it (owner answer
             H-I); past PACKED the carton is theirs, through their own unpack.

             Outline, not fill: undoing her own request is a correction, not the
             ordinary next step. -->
        <f7-block v-else-if="canReopenBox">
          <f7-button
            outline
            large
            :disabled="isReopeningBox"
            @click="confirmReopenBox"
          >
            <f7-preloader
              v-if="isReopeningBox"
              size="20"
              class="margin-right-half"
            ></f7-preloader>
            <f7-icon v-else f7="tray_arrow_up" class="margin-right-half"></f7-icon>
            {{ $t('box.actions.reopen') }}
          </f7-button>
        </f7-block>
      </div>
    </f7-page-content>

    <!-- Thumb-reachable add-box CTA (UX-11) — replaces the old top-corner navbar action.
         Hidden on the empty state, which carries its own full-width add button. -->
    <f7-fab
      v-if="viewMode === 'active' && canOrder && displayedBoxes.length > 0"
      position="right-bottom"
      class="add-box-fab"
      :aria-label="$t('box.actions.addNew')"
      @click="handleAddBoxClick"
    >
      <f7-icon f7="plus"></f7-icon>
      <f7-badge v-if="isAtBoxLimit" color="orange" class="add-box-fab__badge">
        {{ MAX_ACTIVE_BOXES }}
      </f7-badge>
    </f7-fab>

    <!-- Item sheets — ONE page-level instance for details/edit (B7), driven by a
         live ORM lookup so refreshes keep the open sheet current. -->
    <item-sheets
      ref="itemSheetsRef"
      :item="sheetItem"
      :box-status="selectedPackage?.status"
      :box-delivered-confirmed-at="selectedPackage?.deliveredConfirmedAt"
    ></item-sheets>

    <!-- Add New Box Popup -->
    <add-box-popup
      v-model:opened="popupAddBoxOpened"
      :next-box-number="orderBoxes.length + 1"
      :is-creating="isCreatingBox"
      @create="handleCreateBox"
    ></add-box-popup>

    <!-- Advanced Filters Sheet -->
    <f7-sheet
      v-model:opened="sheetAdvancedFiltersOpened"
      backdrop
      class="orders-filters-sheet"
    >
      <f7-toolbar>
        <!--
          The way OUT. This sheet had no close link: dismissal was a downward
          swipe or a backdrop tap, and removing swipe-to-close (it is the one F7
          path that can swallow a scroll gesture) left a 92%-tall sheet whose
          only exit was an 8% strip of backdrop. Every other sheet on this
          pattern already carries this link; `general.close` is the same
          existing key they use.
        -->
        <div class="left">
          <f7-link sheet-close>{{ $t('general.close') }}</f7-link>
        </div>
        <div class="center">{{ $t('filters.title') }}</div>
        <div class="right">
          <f7-link @click="resetAdvancedFilters">{{ $t('general.reset') }}</f7-link>
        </div>
      </f7-toolbar>

      <f7-page-content>
        <!-- Channel Filter -->
        <f7-block-title>{{ $t('filters.channels') }}</f7-block-title>
        <f7-list strong-ios outline-ios dividers-ios>
          <f7-list-item
            v-for="channel in availableChannels"
            :key="channel"
            :title="channel"
            checkbox
            :checked="advancedFilters.selectedChannels.includes(channel)"
            @change="toggleChannel(channel)"
          ></f7-list-item>
        </f7-list>

        <!-- Date Range Filter -->
        <f7-block-title class="margin-top">{{ $t('filters.dateRange') }}</f7-block-title>
        <f7-list strong-ios outline-ios dividers-ios>
          <f7-list-item
            :title="$t('filters.last7Days')"
            radio
            name="date-filter"
            :checked="advancedFilters.dateRange === '7days'"
            @change="advancedFilters.dateRange = '7days'"
          ></f7-list-item>
          <f7-list-item
            :title="$t('filters.last30Days')"
            radio
            name="date-filter"
            :checked="advancedFilters.dateRange === '30days'"
            @change="advancedFilters.dateRange = '30days'"
          ></f7-list-item>
          <f7-list-item
            :title="$t('filters.last90Days')"
            radio
            name="date-filter"
            :checked="advancedFilters.dateRange === '90days'"
            @change="advancedFilters.dateRange = '90days'"
          ></f7-list-item>
          <f7-list-item
            :title="$t('filters.allTime')"
            radio
            name="date-filter"
            :checked="advancedFilters.dateRange === 'all'"
            @change="advancedFilters.dateRange = 'all'"
          ></f7-list-item>
        </f7-list>

      </f7-page-content>

      <!--
        «تطبيق» lives OUTSIDE the scroller. F7-Vue renders the #fixed slot as a
        direct child of .sheet-modal (framework7-vue/components/sheet.js:206-212),
        so it is a SIBLING of the scroll container — which matters here more than
        anywhere: the channel list above is UNBOUNDED, so the distance between
        the member and the apply button used to grow with her own club activity.
        The shared `sheet-fixed-footer` rules live in src/css/app.css.
      -->
      <template #fixed>
        <div class="sheet-fixed-footer">
          <f7-button fill large @click="applyAdvancedFilters">
            {{ $t('filters.apply') }}
          </f7-button>
        </div>
      </template>
    </f7-sheet>
  </f7-page>
</template>

<script setup>
import { useVirtualizer } from '@tanstack/vue-virtual';
import { useEventBus } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { useRepo } from 'pinia-orm';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DecisionsInboxBanner from '@/components/collection/DecisionsInboxBanner.vue';
import LifecycleInfoCard from '@/components/membership/LifecycleInfoCard.vue';
import AddBoxPopup from '@/components/orders/AddBoxPopup.vue';
import CartonDoneOrdering from '@/components/orders/CartonDoneOrdering.vue';
import ItemCard from '@/components/orders/ItemCard.vue';
import ItemSheets from '@/components/orders/ItemSheets.vue';
import PackageCard from '@/components/orders/PackageCard.vue';
import { useCart } from '@/composables/useCart.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { useMembershipStatus } from '@/composables/useMembershipStatus.js';
import { WALLET_REFRESH_EVENT } from '@/constants/wallet.js';
import { OrderBoxStatus } from '@/enums/commerce/OrderBoxStatus.enum.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import { OrderRepository } from '@/repositories/OrderRepository.js';
import {
  cartonFilterPlan,
  ItemFilter,
  matchesItemFilter,
  resolveItemFilter,
} from '@/utils/cartonItemFilters.js';
import { cartonReviewState } from '@/utils/cartonReviewState.js';
import { itemDisplayName, lineNetCents } from '@/utils/orderBoxItemDisplay.js';
import { approvedOutOfStockIds, ShipGate, shipRequestGate } from '@/utils/shipRequestGate.js';
import { contactSellersClubAdmin } from '@/utils/supportContact.js';

// F7-Vue passes f7route / f7router as props to `component:` routes. This page
// is ALSO mounted as the `/orders/` TAB (no boxId param there — see the
// BOX ROUTE section below), so both default to null rather than required.
const props = defineProps({
  f7route: { type: Object, default: null },
  f7router: { type: Object, default: null },
});

// ===========================================================================
// CONSTANTS
// ===========================================================================
const MAX_ACTIVE_BOXES = 3;

// ===========================================================================
// COMPOSABLES & I18N
// ===========================================================================
const { t, locale } = useI18n();

// Chevron glyphs follow reading direction (Arabic = RTL). The buttons are
// already positioned by inline-start/end so their sides auto-flip; this makes
// each glyph point outward toward its own edge without any CSS mirroring.
const isRtl = computed(() => locale.value === 'ar');
const prevChevronIcon = computed(() => (isRtl.value ? 'chevron_right' : 'chevron_left'));
const nextChevronIcon = computed(() => (isRtl.value ? 'chevron_left' : 'chevron_right'));
const { stateKey, canOrder } = useMembershipStatus();
// Legacy alias kept for the lifecycle-info card prop (expects membershipStateKey).
const membershipStateKey = stateKey;
const { formatCurrency } = useCurrency();
const {
  // State
  selectedBoxId,

  // Computed
  orderBoxes,

  // Create
  createBox,
  isCreatingBox,
  createBoxError,

  // Read
  fetchAllBoxes,
  isFetchingAll,
  fetchAllError,
  fetchBox,
  isFetchingBox,

  // Update
  updateBox,
  isUpdatingBox,
  updateBoxError,
  requestShipping,
  isRequestingShipping,
  requestShippingError,
  reopenBox,
  isReopeningBox,
  reopenBoxError,

  // Delete
  closeBox,
  closeBoxError,
  deleteBox,
  deleteBoxError,
} = useCart();
const boxes = useRepo(OrderRepository);
const orders = useRepo(OrderBoxItem);
// ===========================================================================
// LOCAL STATE
// ===========================================================================
const viewMode = ref('active');
// Set to true when a createBox call fails with code 'auth.no_active_membership'.
// Renders a lifecycle info card instead of a toast.
const showMembershipBlockCard = ref(false);
// The chip she TAPPED on this carton, or null while she has tapped none — then
// the carton decides (`activeFilter` below). Reset to null on every carton
// swipe and open/closed switch, so each carton opens on its own default.
const chosenFilter = ref(null);
const selectedPackageIndex = ref(0);
const swiperEl = ref(null);
const popupAddBoxOpened = ref(false);
const sheetAdvancedFiltersOpened = ref(false);

// Track which entity is being modified for loading states
const updatingBoxId = ref(null);

// Item sheets (B7) — the selected row is stored by id and resolved live from
// the ORM, so a fetchBox refresh updates the open sheet instead of showing
// a stale snapshot.
const itemSheetsRef = ref(null);
const sheetItemId = ref(null);

// Advanced filters
const advancedFilters = ref({
  selectedChannels: [],
  dateRange: 'all',
});

// ===========================================================================
// COMPUTED - Item Filters
// ===========================================================================
const FILTER_LABEL_KEYS = Object.freeze({
  [ItemFilter.ALL]: 'filters.all',
  [ItemFilter.PROGRESS]: 'filters.inProgress',
  [ItemFilter.DONE]: 'filters.done',
  [ItemFilter.ISSUES]: 'filters.issues',
  [ItemFilter.CANCELLED]: 'filters.cancelled',
  [ItemFilter.OUT_OF_STOCK]: 'filters.outOfStock',
});

const itemFilters = computed(() =>
  filterPlan.value.filters.map((value) => ({ value, label: t(FILTER_LABEL_KEYS[value]) })),
);

function chooseFilter(value) {
  chosenFilter.value = value;
}

// ===========================================================================
// COMPUTED - Displayed Boxes
// ===========================================================================
const OpenedBoxes = computed(() => boxes.getOpenedBoxes());
const ClosedBoxes = computed(() => boxes.getClosedBoxes());
const displayedBoxes = computed(() => {
  return viewMode.value === 'active' ? OpenedBoxes.value : ClosedBoxes.value;
});

// Box limit checking
const isAtBoxLimit = computed(() => OpenedBoxes.value.length >= MAX_ACTIVE_BOXES);

// ===========================================================================
// COMPUTED / DEEP LINK — the carton route (/orders/box/:boxId/, tips-wave P3-A)
// ===========================================================================
// Present only when this page was reached via the standalone carton route —
// null on the ordinary `/orders/` tab, which carries no boxId param.
const boxRouteId = computed(() => props.f7route?.params?.boxId ?? null);
const isBoxRoute = computed(() => boxRouteId.value != null);

/**
 * Point the swiper at the box the carton route named, in whichever view (open
 * or closed) it lives in. An id that matches nothing — deleted, or a stale
 * link — leaves viewMode/selectedPackageIndex exactly as they were, so the
 * page falls back to the ordinary list, unchanged (owner answer W-K).
 */
function selectBoxById(id) {
  const target = String(id);

  const openedIndex = OpenedBoxes.value.findIndex((box) => String(box.id) === target);
  if (openedIndex !== -1) {
    viewMode.value = 'active';
    selectedPackageIndex.value = openedIndex;
    nextTick(() => swiperEl.value?.swiper?.slideTo(openedIndex, 0));
    return;
  }

  const closedIndex = ClosedBoxes.value.findIndex((box) => String(box.id) === target);
  if (closedIndex !== -1) {
    viewMode.value = 'closed';
    selectedPackageIndex.value = closedIndex;
    nextTick(() => swiperEl.value?.swiper?.slideTo(closedIndex, 0));
  }
}

/**
 * Back from the carton route. Falls back to the ordinary orders list rather
 * than IssueDecisionPage's inbox — this page's own history stack (mirrors
 * that page's `leave()`).
 */
function handleBoxRouteBack() {
  const r = props.f7router;
  if (r?.history && r.history.length > 1) r.back();
  else r?.navigate('/orders/', { clearPreviousHistory: true });
}

// Swipe discoverability (option ج) — the counter pill and prev/next chevrons
// only make sense when there's more than one box to move between. Swiper keeps
// a logical activeIndex (0..n-1) regardless of RTL, so start/end map to the
// index bounds and disable the matching chevron.
const hasMultipleBoxes = computed(() => displayedBoxes.value.length > 1);
const atStart = computed(() => selectedPackageIndex.value <= 0);
const atEnd = computed(() => selectedPackageIndex.value >= displayedBoxes.value.length - 1);

const selectedPackage = computed(() => {
  return displayedBoxes.value[selectedPackageIndex.value] || null;
});
const selectedBoxItems = computed(() => {
  if (!selectedPackage.value?.id) return [];
  return orders.where('orderBoxId', selectedPackage.value.id).orderBy('updatedAt', 'desc').get();
});

const sheetItem = computed(() => (sheetItemId.value ? orders.find(sheetItemId.value) : null));

// The carton «نواقص» tab (carton-oos P1-B). Read off the SAME lines the chip
// counts and the list read, so the chip, its count and the list cannot disagree.
const filterPlan = computed(() => cartonFilterPlan(selectedBoxItems.value, selectedPackage.value));
const activeFilter = computed(() => resolveItemFilter(chosenFilter.value, filterPlan.value));
// The «نواقص» tab on a carton that can still take the item back: the hint, the
// held label and the row buttons all hang on this one fact. A carton that has
// moved on keeps its old missing lines findable under the chip, as plain rows.
const isLiveOosTab = computed(
  () => activeFilter.value === ItemFilter.OUT_OF_STOCK && filterPlan.value.oosLive,
);

// A different carton on screen opens on ITS default. The swipe and the
// open/closed switch already reset through `resetFilters`; this also covers a
// carton that slides into the same index when the one before it is closed or
// deleted, which fires no slide change.
watch(
  () => selectedPackage.value?.id,
  (id, previousId) => {
    if (id !== previousId) chosenFilter.value = null;
  },
);

// ===========================================================================
// COMPUTED - Available Channels
// ===========================================================================
const availableChannels = computed(() => {
  if (!selectedPackage.value) return [];
  const channels = new Set();
  selectedBoxItems.value.forEach((item) => {
    // Manual (off-catalog) lines carry no channel — skip so we don't render an
    // empty channel-filter row.
    if (item.channelName) {
      channels.add(item.channelName);
    }
  });
  return Array.from(channels).sort();
});

// ===========================================================================
// COMPUTED - Advanced Filters Check
// ===========================================================================
const hasAdvancedFilters = computed(() => {
  return (
    advancedFilters.value.selectedChannels.length > 0 || advancedFilters.value.dateRange !== 'all'
  );
});

// ===========================================================================
// COMPUTED - Filtered Items
// ===========================================================================
const filteredItems = computed(() => {
  if (!selectedPackage.value) return [];
  let items = selectedBoxItems.value;

  // Apply basic status filter — the same matcher the chip counts read.
  items = items.filter((item) => matchesItemFilter(item, activeFilter.value));

  // Apply channel filter
  if (advancedFilters.value.selectedChannels.length > 0) {
    items = items.filter((item) =>
      advancedFilters.value.selectedChannels.includes(item.channelName),
    );
  }

  // Date filtering - placeholder for when date fields are available
  if (advancedFilters.value.dateRange !== 'all') {
    // TODO: Implement date filtering when createdAt/updatedAt fields are available
    // Example: filter items based on item.createdAt
  }

  //const itms = useRepo(OrderBoxItem).with('product').with('variant').whereId(useKeys(items)).get();
  //console.log(itms);
  // console.log(useRepo(Variant).find(41787005682776));
  return items;
});

// ===========================================================================
// COMPUTED - Filtered Items Summary
// ===========================================================================

const filteredItemsQty = computed(() => {
  return filteredItems.value.reduce((sum, item) => sum + item.qty, 0);
});

const formatFilteredTotal = computed(() => {
  // Net line totals (post-discount) — a Poola line contributes the cash paid,
  // not its full retail price. Identical to price*qty for normal lines.
  const total = filteredItems.value.reduce((sum, item) => {
    return sum + lineNetCents(item);
  }, 0);
  return formatCurrency(total);
});

// ===========================================================================
// COMPUTED - Request Shipping Check
// ===========================================================================
const canRequestShipping = computed(() => {
  return selectedPackage.value?.status === OrderBoxStatus.ACTIVE;
});

// The ask-to-ship gate ("ready to pack", ship-ready pack). Which of the button's
// states the selected carton is in, decided from its own lines — see
// `shipRequestGate` for the order. `isFetchingBox` is the "lines are still coming
// in" signal: before this, the button decided on whatever was in the store and the
// dialog could open before the fetch returned, naming no missing lines because
// none had loaded yet. The server stays the authority and answers 409 with her
// sentence if this ever disagrees with it.
const shipGate = computed(() =>
  shipRequestGate(selectedPackage.value?.items, { loading: isFetchingBox.value }),
);

// Where the selected ACTIVE carton stands in the two-lane cycle (done-ordering
// pack): open / closed and waiting / closed and being prepared / the team's review.
// `null` for a carton that is not ACTIVE and for Poola — then the slot is today's.
const reviewState = computed(() =>
  cartonReviewState(selectedPackage.value, { loading: isFetchingBox.value }),
);

// A successful ask-to-ship can cancel out-of-stock lines, and the money held for
// them returns to her AVAILABLE balance without a wallet transaction — a derived
// number nothing local can compute. The wallet tab (kept alive once loaded) hears
// this and re-reads its own balance: the same signal the single-line cancel fires
// from ItemSheets.
const walletRefreshBus = useEventBus(WALLET_REFRESH_EVENT);

// ===========================================================================
// ITEMS LIST VIRTUALIZATION (P9 — mirrors VirtualPostList.vue)
// ===========================================================================
// The scroll element is the page-content; the list starts below the swiper +
// summary card, so the virtualizer needs scrollMargin = the list's offset
// inside the scroll content. TanStack includes scrollMargin in each row's
// `start`, hence the `start - listScrollMargin` transform in the template,
// while getTotalSize() excludes it (the ul height).
const ESTIMATED_ITEM_HEIGHT = 116;

const pageContentRef = ref(null);
const aboveListRef = ref(null);
const itemsListRef = ref(null);
const listScrollMargin = ref(0);

const virtualizerOptions = computed(() => ({
  count: filteredItems.value.length,
  getScrollElement: () => pageContentRef.value?.$el ?? null,
  estimateSize: () => ESTIMATED_ITEM_HEIGHT,
  overscan: 4,
  scrollMargin: listScrollMargin.value,
  measureElement: (el) => el?.getBoundingClientRect?.().height || ESTIMATED_ITEM_HEIGHT,
}));

const virtualizer = useVirtualizer(virtualizerOptions);
const virtualRows = computed(() => virtualizer.value.getVirtualItems());
const itemsTotalSize = computed(() => virtualizer.value.getTotalSize());

// Template :ref on each row — ItemCard is single-root, so $el is the <li>
// (carrying the fallthrough data-index the virtualizer maps measurements by).
function measureRow(componentRef) {
  const el = componentRef?.$el;
  if (el instanceof HTMLElement && virtualizer.value?.measureElement) {
    virtualizer.value.measureElement(el);
  }
}

function updateListScrollMargin() {
  const scrollEl = pageContentRef.value?.$el;
  const listEl = itemsListRef.value?.$el;
  if (!scrollEl?.getBoundingClientRect || !listEl?.getBoundingClientRect) return;
  const margin =
    listEl.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top + scrollEl.scrollTop;
  if (margin >= 0) {
    listScrollMargin.value = margin;
  }
}

// The content above the list (swiper, summary, chips) loads/resizes async —
// re-measure the list offset whenever it changes.
let aboveListResizeObserver = null;

watch([selectedPackage, () => filteredItems.value.length], async () => {
  await nextTick();
  updateListScrollMargin();
});

// ===========================================================================
// HELPER FUNCTIONS
// ===========================================================================

// Swipe discoverability (option ج) — tap-nav that mirrors the swipe gesture.
// slidePrev/slideNext move by logical index, so they respect the RTL layout
// automatically (in RTL, slideNext advances to the box on the left).
function goPrevBox() {
  swiperEl.value?.swiper?.slidePrev();
}

function goNextBox() {
  swiperEl.value?.swiper?.slideNext();
}

const getFilterCount = (filterValue) => {
  if (!selectedPackage.value) return 0;
  return selectedBoxItems.value.filter((item) => matchesItemFilter(item, filterValue)).length;
};

// (The ship-request gate that used to live here as `getIssuesCount` is
// `shipGate` above, built on `shipRequestGate`. It reads the same
// ItemStatusCategories.BLOCKS_SHIPPING list for the ISSUE count, so this button
// and the PackageCard banner still always agree; OUT_OF_STOCK is still not an
// issue and never blocks.)

// ===========================================================================
// TOAST HELPERS
// ===========================================================================
const showSuccessToast = (message) => {
  f7.toast
    .create({
      text: message,
      position: 'center',
      closeTimeout: 2000,
      cssClass: 'toast-success',
    })
    .open();
};

const showErrorToast = (message, closeTimeout = 3000) => {
  f7.toast
    .create({
      text: message,
      position: 'center',
      closeTimeout,
      cssClass: 'toast-error',
    })
    .open();
};

// ===========================================================================
// LIFECYCLE CTA HANDLERS
// ===========================================================================
const handleAwaitingDepositCta = () => {
  f7.views.main.router.navigate('/wallet/?autoOpenDeposit=1');
};

const handleSupportCta = () => {
  contactSellersClubAdmin();
};

// ===========================================================================
// CREATE HANDLERS
// ===========================================================================
// Show box limit information dialog
const showBoxLimitDialog = () => {
  f7.dialog
    .create({
      title: t('box.limit.title'),
      text: `
      <div style="text-align: start; line-height: 1.6;">
        <p style="margin-bottom: 16px; font-size: 15px;">
          ${t('box.limit.message', { current: OpenedBoxes.value.length, max: MAX_ACTIVE_BOXES })}
        </p>
        <p style="margin-bottom: 12px; font-weight: 600; color: var(--f7-theme-color);">
          ${t('box.limit.suggestion')}
        </p>
        <ul style="list-style: none; padding: 0; margin: 0;">
          <li style="padding: 8px 0; display: flex; align-items: center;">
            <i class="f7-icons" style="font-size: 20px; margin-inline-end: 8px; color: var(--f7-theme-color);">checkmark_circle</i>
            <span>${t('box.limit.suggestions.requestShipping')}</span>
          </li>
          <li style="padding: 8px 0; display: flex; align-items: center;">
            <i class="f7-icons" style="font-size: 20px; margin-inline-end: 8px; color: var(--f7-theme-color);">checkmark_circle</i>
            <span>${t('box.limit.suggestions.closeBox')}</span>
          </li>
          <li style="padding: 8px 0; display: flex; align-items: center;">
            <i class="f7-icons" style="font-size: 20px; margin-inline-end: 8px; color: var(--f7-theme-color);">checkmark_circle</i>
            <span>${t('box.limit.suggestions.waitForShipping')}</span>
          </li>
        </ul>
      </div>
    `,
      buttons: [
        {
          text: t('box.limit.action.understand'),
          close: true,
        },
      ],
      verticalButtons: false,
    })
    .open();
};

// Handle add box button click with limit check
const handleAddBoxClick = () => {
  if (isAtBoxLimit.value) {
    showBoxLimitDialog();
    return;
  }
  popupAddBoxOpened.value = true;
};

const handleCreateBox = async (boxData) => {
  showMembershipBlockCard.value = false;
  try {
    const newBox = await createBox(0, boxData);

    // Gated (e.g. account suspended) — guard already toasted; skip success path.
    if (!newBox && !createBoxError.value) {
      popupAddBoxOpened.value = false;
      return;
    }

    if (createBoxError.value) {
      // Membership not active — suppress toast, surface lifecycle info card instead.
      if (createBoxError.value.code === 'auth.no_active_membership') {
        popupAddBoxOpened.value = false;
        showMembershipBlockCard.value = true;
        return;
      }

      // Check if error is about box limit
      const errorMessage = createBoxError.value.message || '';
      if (
        errorMessage.includes('Max Active order boxes count') ||
        errorMessage.includes('maximum')
      ) {
        popupAddBoxOpened.value = false;
        showBoxLimitDialog();
        return;
      }

      showErrorToast(createBoxError.value.message || t('box.feedback.errorCreating'));
      return;
    }

    popupAddBoxOpened.value = false;
    showSuccessToast(t('box.feedback.created'));

    // Switch to active view to show new box
    viewMode.value = 'active';

    // Refresh to get latest data
    await fetchAllBoxes(0, true);
  } catch (error) {
    console.error('Error creating box:', error);

    // Membership not active — suppress toast, surface lifecycle info card instead.
    if (error?.code === 'auth.no_active_membership') {
      popupAddBoxOpened.value = false;
      showMembershipBlockCard.value = true;
      return;
    }

    // Check if error is about box limit
    const errorMessage = error.message || error.toString();
    if (errorMessage.includes('Max Active order boxes count') || errorMessage.includes('maximum')) {
      popupAddBoxOpened.value = false;
      showBoxLimitDialog();
      return;
    }

    showErrorToast(t('box.feedback.errorCreating'));
  }
};

// ===========================================================================
// UPDATE HANDLERS
// ===========================================================================
const handleUpdateBox = async (updatedBox) => {
  updatingBoxId.value = updatedBox.id;
  try {
    await updateBox(0, updatedBox.id, updatedBox);

    if (updateBoxError.value) {
      showErrorToast(updateBoxError.value.message || t('box.feedback.errorUpdating'));
      return;
    }

    showSuccessToast(t('box.feedback.updated'));

    // Refresh the specific box
    await fetchBox(0, updatedBox.id, true);
  } catch (error) {
    console.error('Error updating box:', error);
    showErrorToast(t('box.feedback.errorUpdating'));
  } finally {
    updatingBoxId.value = null;
  }
};

// Row tap (B7): remember the item by id and open the shared details sheet.
const handleItemSelect = (item) => {
  sheetItemId.value = item.id;
  itemSheetsRef.value?.openDetails();
};

// The «نواقص» tab's row buttons. The row names the item; the sheet host does the
// rest through the SAME door and the SAME one-layer confirm its details sheet
// uses. `nextTick` first, so the host's `item` prop is this row's before it acts.
const handleItemSuggest = async (item) => {
  sheetItemId.value = item.id;
  await nextTick();
  itemSheetsRef.value?.openSuggest();
};

const handleItemCancel = async (item) => {
  sheetItemId.value = item.id;
  await nextTick();
  itemSheetsRef.value?.confirmCancelOutOfStock();
};

// Delete Handlers
const handleCloseBox = async (boxId) => {
  f7.dialog.confirm(t('box.confirmations.close'), t('general.confirm'), async () => {
    try {
      await closeBox(0, boxId);

      if (closeBoxError.value) {
        showErrorToast(closeBoxError.value.message || t('box.feedback.errorClosing'));
        return;
      }

      showSuccessToast(t('box.feedback.closed'));
      await fetchAllBoxes(0, true);
    } catch (error) {
      console.error('Error closing box:', error);
      showErrorToast(t('box.feedback.errorClosing'));
    }
  });
};

const handleDeleteBox = async (boxId) => {
  f7.dialog.confirm(t('box.confirmations.delete'), t('general.warning'), async () => {
    try {
      await deleteBox(boxId);

      if (deleteBoxError.value) {
        showErrorToast(deleteBoxError.value.message || t('box.feedback.errorDeleting'));
        return;
      }

      showSuccessToast(t('box.feedback.deleted'));
      await fetchAllBoxes(0, true);
    } catch (error) {
      console.error('Error deleting box:', error);
      showErrorToast(t('box.feedback.errorDeleting'));
    }
  });
};

// ── BEFORE THE CARTON LEAVES (oos-hold P2-B, owner answer H-I; reshaped by the
//    ship-ready pack, prompt 3) ─────────────────────────────────────────────────
//
// Asking to ship now means "ready to pack", and it CANCELS the lines nobody could
// find — with her approval, given here. The lines the confirm shows (from the box
// this page already holds) are the lines she approves: the dialog's text, the
// button label and the ids sent all come from ONE snapshot taken when the dialog
// opens, so what she read is exactly what is sent. The server checks the other
// direction — every line out of stock at that moment must be in the list, or it
// refuses and cancels nothing (a line ran out while she looked).
//
// The backend's `orderbox.ready.missing_items` push is now near-silent (a carton
// has no out-of-stock line after a successful request) and was never the primary
// surface.

/**
 * Escaped, because `dialog.create` takes `text` as HTML and an item name is
 * member-entered on a manual line. The rest of this page's rich dialog
 * (`showBoxLimitDialog`) interpolates only i18n strings, so this is the first
 * place on the surface where that matters.
 */
const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));

/**
 * The confirm body: the ordinary sentence, or — when the carton holds lines
 * nobody could find — the sentence that they will be CANCELLED and their held
 * money returns to her balance, followed by their names.
 *
 * @param {Array<object>} outOfStock the lines being shown (and approved)
 */
const requestShippingText = (outOfStock) => {
  if (outOfStock.length === 0) {
    return escapeHtml(t('box.actions.confirmRequestShipping'));
  }

  const names = outOfStock
    .map((item) => `<li>${escapeHtml(itemDisplayName(item))}</li>`)
    .join('');

  return '<div style="text-align: start; line-height: 1.6;">'
    + `<p style="margin: 0 0 8px;">${escapeHtml(t('box.actions.confirmRequestShippingCancelMissing'))}</p>`
    + `<ul style="margin: 0; padding-inline-start: 18px;">${names}</ul>`
    + '</div>';
};

// Request Shipping
//
// `dialog.create` with explicit buttons rather than `dialog.confirm`: the
// shorthand renders English OK/Cancel regardless of locale (the defect
// WalletPage documents at its own confirm), and it cannot carry the item list.
const confirmRequestShipping = () => {
  const box = selectedPackage.value;
  // The button is only offered in READY, so this is the belt to its braces: a
  // second tap, or a realtime update that landed between the render and the tap.
  if (!box || shipGate.value.state !== ShipGate.READY) return;

  // Frozen now. She may swipe to another carton, or a line may change under the
  // open dialog — what she approves is what she was shown, on the carton she was
  // looking at.
  const boxId = box.id;
  const shown = [...shipGate.value.outOfStock];
  const approvedIds = approvedOutOfStockIds(shown);
  const confirmLabel = shown.length > 0
    ? t('box.actions.confirmCancelMissingAndShip')
    : t('general.confirm');
  // With lines to cancel, the confirm button itself says "cancel them" — so the
  // dismiss button is «رجوع», not «إلغاء»: two cancel verbs side by side would
  // leave her unsure which one gives up the request and which gives up the lines.
  const dismissLabel = shown.length > 0 ? t('general.back') : t('general.cancel');

  f7.dialog
    .create({
      title: t('general.confirm'),
      text: requestShippingText(shown),
      buttons: [
        { text: dismissLabel, close: true },
        {
          text: confirmLabel,
          close: true,
          onClick: () => runRequestShipping(boxId, approvedIds),
        },
      ],
      verticalButtons: false,
    })
    .open();
};

const runRequestShipping = async (boxId, approvedIds = []) => {
  if (boxId == null) return;
  try {
    await requestShipping(0, boxId, approvedIds);

    // A refusal is a 409 carrying HER sentence in her language (lines still on
    // the way / a line ran out while she looked / nothing arrived / the carton
    // is no longer open), and `errorFromJsonApi` has already made it `.message`
    // — printed verbatim, never replaced by ours. It can be a long sentence (it
    // names up to three lines), so the toast stays up longer than the default.
    //
    // The carton is re-read either way: on a refusal this device's picture of it
    // is the thing that was wrong, and its own lines now show what is where.
    if (requestShippingError.value) {
      showErrorToast(
        requestShippingError.value.message || t('box.feedback.errorRequestingShipping'),
        6000,
      );
      await fetchBox(0, boxId, true);
      return;
    }

    showSuccessToast(t('box.feedback.shippingRequested'));
    await fetchBox(0, boxId, true);
    walletRefreshBus.emit();
  } catch (error) {
    console.error('Error requesting shipping:', error);
    // `useAsyncState` swallows the API's own errors into `requestShippingError`
    // above, so what lands here is a local failure — never something to print raw.
    showErrorToast(t('box.feedback.errorRequestingShipping'));
    await fetchBox(0, boxId, true).catch(() => {});
  }
};

// ── AND THE WAY BACK ────────────────────────────────────────────────────────
const canReopenBox = computed(
  () => selectedPackage.value?.status === OrderBoxStatus.READY,
);

const confirmReopenBox = () => {
  if (!selectedPackage.value) return;

  f7.dialog
    .create({
      title: t('general.confirm'),
      text: t('box.confirmations.reopen'),
      buttons: [
        { text: t('general.cancel'), close: true },
        { text: t('general.confirm'), close: true, onClick: runReopenBox },
      ],
      verticalButtons: false,
    })
    .open();
};

const runReopenBox = async () => {
  const boxId = selectedPackage.value?.id;
  if (boxId == null) return;

  try {
    await reopenBox(0, boxId);

    // A 409 means the carton moved on underneath her — sealed, shipped, or
    // already open — and its body is an Arabic sentence meant to be printed
    // VERBATIM. `errorFromJsonApi` already prefers the server's `detail`, so
    // `message` IS that sentence. The box is re-read either way: on success the
    // line statuses all changed, and on a 409 this device's picture of the
    // carton is the thing that was wrong.
    if (reopenBoxError.value) {
      showErrorToast(reopenBoxError.value.message || t('box.feedback.errorReopening'));
      await fetchBox(0, boxId, true);
      return;
    }

    showSuccessToast(t('box.feedback.reopened'));
    await fetchBox(0, boxId, true);
  } catch (error) {
    console.error('Error reopening box:', error);
    showErrorToast(error?.message || t('box.feedback.errorReopening'));
    await fetchBox(0, boxId, true).catch(() => {});
  }
};

// Filter Handlers
const toggleChannel = (channel) => {
  const index = advancedFilters.value.selectedChannels.indexOf(channel);
  if (index > -1) {
    advancedFilters.value.selectedChannels.splice(index, 1);
  } else {
    advancedFilters.value.selectedChannels.push(channel);
  }
};

const resetAdvancedFilters = () => {
  advancedFilters.value = {
    selectedChannels: [],
    dateRange: 'all',
  };
};

const applyAdvancedFilters = () => {
  sheetAdvancedFiltersOpened.value = false;
  if (hasAdvancedFilters.value) {
    showSuccessToast(t('filters.applied'));
  }
};

// Event Handlers
const resetFilters = () => {
  chosenFilter.value = null;
  resetAdvancedFilters();
};

const onPackageChange = (event) => {
  const [swiper] = event.detail;
  selectedPackageIndex.value = swiper.activeIndex;

  const newBox = displayedBoxes.value[swiper.activeIndex];
  if (newBox) {
    selectedBoxId.value = newBox.id;
  }
  resetFilters();
};

const handlePullToRefresh = async (done) => {
  try {
    await fetchAllBoxes(0, true);
    await fetchBox(0, selectedPackage.value.id, true);

    if (fetchAllError.value) {
      showErrorToast(fetchAllError.value.message || t('box.feedback.errorRefreshing'));
    }
  } catch (error) {
    console.error('Error refreshing:', error);
    showErrorToast(t('box.feedback.errorRefreshing'));
  } finally {
    done();
  }
};

const handleRefreshClick = async () => {
  try {
    await fetchAllBoxes(0, true);
    await fetchBox(0, selectedPackage.value.id, true);

    if (fetchAllError.value) {
      showErrorToast(fetchAllError.value.message || t('box.feedback.errorRefreshing'));
    } else {
      showSuccessToast(t('box.feedback.refreshedSuccess'));
    }
  } catch (error) {
    console.error('Error refreshing:', error);
    showErrorToast(t('box.feedback.errorRefreshing'));
  }
};

// Watchers
watch(
  selectedPackage,
  async (newPackage) => {
    if (newPackage?.id) {
      selectedBoxId.value = newPackage.id;
    }
  },
  { immediate: true },
);

watch(viewMode, () => {
  selectedPackageIndex.value = 0;
  swiperEl.value?.swiper?.slideTo(0, 0);
  resetFilters();
});

// Retry after a failed first load — the inline error state (not a toast) owns
// this failure mode; useAsyncState clears fetchAllError on re-execute.
const handleRetryFetch = async () => {
  await fetchAllBoxes(0, true);
};

// Lifecycle
onMounted(async () => {
  if (typeof ResizeObserver !== 'undefined' && aboveListRef.value) {
    aboveListResizeObserver = new ResizeObserver(() => updateListScrollMargin());
    aboveListResizeObserver.observe(aboveListRef.value);
  }

  await fetchAllBoxes(0);
  if (boxRouteId.value != null) selectBoxById(boxRouteId.value);
  await nextTick();
  updateListScrollMargin();
});

onBeforeUnmount(() => {
  if (aboveListResizeObserver) {
    aboveListResizeObserver.disconnect();
    aboveListResizeObserver = null;
  }
});
</script>

<style scoped>
/*
 * Advanced-filters sheet. DEFINITE height, deliberately — not the `height: auto;
 * max-height: 92%` it used to hug its content with. With a definite height
 * Framework7's own chain produces the scroller (.sheet-modal-inner 100% ->
 * .page-content 100%; overflow:auto), so `sheet-scroll` came off: that utility
 * exists only to rescue AUTO-height sheets, and there is no auto height left.
 *
 * 92%, the tallest of the three sheets in this pack: the channel list is
 * unbounded — it is one row per channel the member actually orders from.
 *
 * The unit is a PERCENTAGE of the app root, never vh/dvh: inside Telegram
 * `html.telegram-webapp` sizes that root to the visible band while vh measures
 * the whole WebView. tests/unit/styles/sheet-scroll-invariant.test.js enforces it.
 *
 * Kept in CSS rather than an inline style: Vue parses a static style attribute
 * into an OBJECT, so a property declared twice there silently collapses to the
 * last one. Nothing needs two declarations today, but an inline cap is a trap
 * for whoever does.
 */
.orders-filters-sheet {
  height: 92%;

  /* Framework7's .page-content folds this straight into its own padding-bottom
     (framework7-bundle.css:391), so the last date radio clears the sticky footer
     instead of hiding under it. Keep it in step with the footer's height in
     app.css: that is this var PLUS --f7-safe-area-bottom, and .page-content
     already adds that inset itself. */
  --sheet-fixed-footer-h: 76px;
  --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

/*
 * Tells the member what to say when they land in the chat. Telegram can't pre-fill
 * a message for a personal account, so without this they arrive at an empty box.
 * Caption for the CTA directly below it, not body copy.
 */
.lifecycle-cta-hint {
  color: #666;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 auto 8px auto;
  max-width: 320px;
}

/* The one helper line under the disabled ask-to-ship button while lines are
   still on their way (ship-ready pack) — what she can do about it. */
.ship-gate-help {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}

/* Self-serve deposit — kept available, deliberately quieter than the contact CTA. */
.lifecycle-cta-secondary {
  margin-top: 8px;
}

/* Subnavbar Content */
.margin-bottom {
  margin-bottom: 12px;
}

/* Enhanced Filters Wrapper */
.filters-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  margin-bottom: 8px;
}

/* Chips Container */
.chips-container {
  flex: 1;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.chips-container::-webkit-scrollbar {
  display: none;
}

/* «نواقص» while the carton can still take a missing item back: first in the
   row and amber, not red — the club is still looking, nothing has failed yet. */
.filter-chip--oos {
  --f7-chip-bg-color: var(--poolit-warning-700, #b45309);
  --f7-chip-text-color: #ffffff;
  --f7-chip-outline-border-color: var(--poolit-warning-600, #d97706);
  --f7-chip-outline-text-color: var(--poolit-warning-700, #b45309);
  font-weight: 600;
}

.filter-chip--oos:not(.chip-outline) .badge {
  background-color: #ffffff;
  color: var(--poolit-warning-700, #b45309);
}

.filter-chip--oos.chip-outline .badge {
  background-color: var(--poolit-warning-700, #b45309);
  color: #ffffff;
}

/* The «نواقص» tab's one explanation. */
.oos-hint {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 16px 8px;
  padding: 12px 14px;
  border: 1px solid #f6dfa6;
  border-radius: 12px;
  background: var(--poolit-warning-50, #fffbeb);
}

.oos-hint__icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--poolit-warning-100, #fef3c7);
  color: var(--poolit-warning-700, #b45309);
}

.oos-hint__text {
  min-width: 0;
}

.oos-hint__title {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 700;
  color: #7c2d12;
}

.oos-hint__body {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  color: #4a3a22;
}

.dark .oos-hint {
  border-color: rgba(245, 158, 11, 0.3);
  background: rgba(245, 158, 11, 0.12);
}

.dark .oos-hint__icon {
  background: rgba(245, 158, 11, 0.2);
  color: #fcd34d;
}

.dark .oos-hint__title {
  color: #fcd34d;
}

.dark .oos-hint__body {
  color: rgba(255, 255, 255, 0.82);
}

/* Filter Button Wrapper */
.filter-button-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

/*
 * Where the content starts under the bars. Framework7's .page-content already
 * pads itself by navbar + top toolbar + subnavbar (the last one because the
 * page declares `with-subnavbar`), and folds this variable into the same sum —
 * so this is the ONLY spacing the page adds, and it tracks the notch, the
 * Telegram top inset and the act-as banner for free. Set on the scroller, not
 * the page: the sheets below are its siblings and must not inherit it.
 *
 * Do not re-add a navbar-offset padding to a child (the carton swiper carried
 * one for years). It doubles the offset, and whatever is placed above that
 * child is left behind the bars.
 */
.orders-page-content {
  --f7-page-content-extra-padding-top: 12px;
}

/* Side gutter for the decisions banner, matching the chips row below. Inline
   padding only: with the banner hidden the slot is 0px tall. */
.orders-decisions-slot {
  padding-inline: var(--poolit-spacing-md, 16px);
}

/* Packages Swiper */
.packages-swiper-wrapper {
  width: 100%;
}

/* Swipe discoverability — persistent counter pill (box.swipe.counter) */
.swipe-counter-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: fit-content;
  margin: 0 auto 8px;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--poolit-bg-secondary);
  border: var(--poolit-border-width-thin) solid var(--poolit-border-color);
  color: var(--poolit-text-muted);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
}

.swipe-counter-pill__icon {
  color: var(--poolit-text-muted);
}

/* Swiper's native pagination dots — F7 v9's `swiper-container` renders bullets
   in an open shadow root, but these `--swiper-pagination-*` custom properties
   cross the boundary and brand the dots (active = theme color, inactive =
   muted). Bullet width is uniform by design; the "كرتونة X من Y" counter above
   the swiper carries the position signal. */
.packages-swiper-wrapper swiper-container {
  --swiper-pagination-color: var(--f7-theme-color);
  --swiper-pagination-bullet-inactive-color: var(--poolit-text-muted);
  --swiper-pagination-bullet-inactive-opacity: 0.35;
  --swiper-pagination-bullet-opacity: 1;
  --swiper-pagination-bullet-width: 8px;
  --swiper-pagination-bullet-height: 8px;
  --swiper-pagination-bullet-horizontal-gap: 5px;
  --swiper-pagination-bottom: 6px;
}

/* Swipe discoverability (option ج) — the swiper + edge chevrons live in a
   positioned viewport so the chevrons anchor to the card, not the page. */
.swipe-viewport {
  position: relative;
}

/* Prev/next chevrons — vertically centered on the card body (top: 50% of the
   viewport, which is the swiper/card height; the counter pill sits ABOVE the
   viewport so it doesn't shift this center up). Sat at the inline edges so the
   sides follow text direction automatically. */
.swipe-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--f7-theme-color);
  color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition:
    opacity var(--poolit-transition-fast) var(--poolit-transition-ease),
    transform var(--poolit-transition-fast) var(--poolit-transition-ease);
}

.swipe-nav:active {
  transform: translateY(-50%) scale(0.92);
}

.swipe-nav--prev {
  inset-inline-start: 4px;
}

.swipe-nav--next {
  inset-inline-end: 4px;
}

.swipe-nav--disabled {
  opacity: 0.32;
  box-shadow: none;
  cursor: default;
}

/* Items Section — bottom padding keeps the request-shipping button clear of the FAB */
.items-section {
  padding-top: 8px;
  padding-bottom: calc(56px + var(--poolit-spacing-xl));
}

/* Virtualized items list (P9): f7-list's own <ul> becomes the positioning
   context sized to the virtualizer's total height; rows are absolutely
   positioned <li> translated to their virtual offsets. Keeping ul > li
   intact preserves the F7 media-list / hairline styling. */
.items-virtual-list :deep(ul) {
  position: relative;
  height: var(--items-virtual-height, auto);
}

.items-virtual-list :deep(.items-virtual-row) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  contain: layout style;
}

/* Items Summary Card */
.items-summary-card {
  margin: 8px 16px 12px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--f7-theme-color-tint) 0%, var(--f7-theme-color) 100%);
  box-shadow: 0 2px 12px rgba(var(--f7-theme-color-rgb), 0.15);
  overflow: hidden;
}

.summary-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 16px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.summary-item-highlight {
  flex: 1.2;
}

.summary-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.summary-icon-items {
  background: rgba(255, 255, 255, 0.2);
}

.summary-icon-amount {
  background: rgba(255, 255, 255, 0.25);
}

.summary-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
}

.summary-value-primary {
  font-size: 22px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.summary-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  text-transform: capitalize;
  letter-spacing: 0.3px;
}

.summary-divider {
  width: 1px;
  height: 40px;
  background: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
}

/* Toast Styles */
:deep(.toast-success) {
  background: var(--f7-theme-color);
}

:deep(.toast-error) {
  background: #ff3b30;
}

/* Dark Mode Adjustments */
.dark .items-summary-card,
.theme-dark .items-summary-card {
  background: linear-gradient(135deg, var(--f7-theme-color-shade) 0%, var(--f7-theme-color) 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
}

/* Responsive Design */
@media (max-width: 374px) {
  .items-summary-card {
    margin: 6px 12px 10px 12px;
  }

  .summary-content {
    padding: 10px 12px;
    gap: 12px;
  }

  .summary-icon-wrapper {
    width: 32px;
    height: 32px;
  }

  .summary-value {
    font-size: 18px;
  }

  .summary-value-primary {
    font-size: 20px;
  }

  .summary-label {
    font-size: 11px;
  }

  .summary-divider {
    height: 36px;
  }
}

@media (min-width: 768px) {
  .items-summary-card {
    max-width: 568px;
    margin-left: auto;
    margin-right: auto;
  }
}
/* Navbar Right Section Layout */
:deep(.navbar-inner .right) {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-inline-start: 8px;
}

/* Icon-only navbar links — enforce a 44px tap target (WCAG 2.5.5) */
.nav-icon-link {
  min-width: 44px;
  min-height: 44px;
  justify-content: center;
}

/* First-load skeleton sized like a package card */
.package-skeleton {
  height: 200px;
  border-radius: var(--poolit-radius-lg);
  margin: var(--poolit-spacing-md);
}

/* Boxes load-failure state */
.boxes-error-state {
  padding-top: var(--poolit-spacing-xxl);
}

.boxes-error-state .retry-button {
  margin-top: var(--poolit-spacing-md);
}

/* Thumb-reachable add-box FAB — lifted above the shell tabbar + iOS safe area
   (same offset pattern as OpenInBrowserFab.vue) */
.add-box-fab.fab-right-bottom {
  bottom: calc(
    var(--f7-tabbar-labels-height, 50px) + var(--f7-safe-area-bottom, 0px) +
      var(--poolit-spacing-md)
  );
}

.add-box-fab__badge {
  position: absolute;
  top: -4px;
  inset-inline-end: -4px;
  font-size: var(--poolit-font-size-small);
  min-width: 18px;
  height: 18px;
  line-height: 18px;
}

/* Refresh Button Styling */
/* Rotation Animation for Loading State */
:deep(.rotating-icon) {
  animation: rotating 1s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* BLOCKED lifecycle banner */
.lifecycle-blocked-banner {
  background: #dc2626;
  height: 36px;
}

.lifecycle-blocked-banner__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  padding: 0 16px;
}

.lifecycle-blocked-banner__icon {
  color: #fff;
  flex-shrink: 0;
}

.lifecycle-blocked-banner__text {
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
